import React, { useMemo } from 'react';

import { DateFormat, TimeFormat } from '@aline/common';
import { Divider, useTheme } from '@mui/material';
import { compareAsc, format, parseISO } from 'date-fns';
import { sort, uniqWith } from 'ramda';
import { AlineAvatar, AlineAvatarGroup } from 'src/components';
import { useEditorTabStateActions } from 'src/editor/editor-tab-state-registry';
import { Apollo } from 'src/gql/generated';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(theme => ({
  divider: {
    width: '100%',
  },
  lastReplyTime: {
    color: theme.appColors.iconsType,
    display: 'flex',
    flexDirection: 'column',
    fontFamily: theme.typography.fontFamily,
    fontSize: 10,
    marginRight: theme.spacing(1),
  },
  numReplies: {
    color: theme.appColors.primary,
    fontFamily: theme.typography.fontFamily,
    fontSize: 10,
    fontWeight: 600,
    marginRight: theme.spacing(1),
  },
  repliesContainer: {
    '&:hover': {
      backgroundColor: theme.appColors.hoverBackground,
    },
    alignItems: 'center',
    cursor: 'pointer',
    display: 'flex',
    justifyContent: 'space-between',
    padding: theme.spacing(1),
  },
}));

interface NumRepliesProps {
  rootCommentId: string;
}

export function NumReplies(props: NumRepliesProps): JSX.Element | null {
  const { rootCommentId } = props;

  const { classes } = useStyles();
  const theme = useTheme();
  const {
    comments: { setActivePanel, setCommentThreadId },
  } = useEditorTabStateActions();
  const { data } = Apollo.useGetCommentRepliesQuery({
    variables: { where: { id: rootCommentId } },
  });

  const repliesSortedByCreatedAt = useMemo(() => {
    const replies = data?.comment?.replies ?? [];

    return sort((a, b) => compareAsc(parseISO(a.createdAt), parseISO(b.createdAt)), replies);
  }, [data?.comment?.replies]);

  if (!repliesSortedByCreatedAt.length) return null;

  // Leave last reply time below the empty check in case replies is an empty array.
  const lastReply = repliesSortedByCreatedAt[repliesSortedByCreatedAt.length - 1];
  const timeDisplay = `${DateFormat.FULL_DATE_ABBREV_MONTH} ${TimeFormat.CLOCK_12}`;
  const lastReplyTime = format(parseISO(lastReply.createdAt), timeDisplay);
  const authorNamesSet = uniqWith(
    (a, b) => a.authorName === b.authorName,
    repliesSortedByCreatedAt.flatMap(reply => {
      const authorName = reply.author?.displayName ?? reply.importedAuthorName;
      return authorName ? [{ authorName, avatarColor: reply.author?.avatarColor }] : [];
    })
  );
  const replyText =
    repliesSortedByCreatedAt.length === 1
      ? '1 reply'
      : `${repliesSortedByCreatedAt.length} replies`;

  return (
    <>
      <Divider className={classes.divider} orientation="horizontal" />
      <div
        className={classes.repliesContainer}
        onClick={e => {
          e.stopPropagation();
          setCommentThreadId(rootCommentId);
          setActivePanel('ViewThread');
        }}
      >
        <span className={classes.numReplies}>{replyText}</span>
        <div className={classes.lastReplyTime}>
          <span>Last reply</span>
          <span>{`${lastReplyTime}`}</span>
        </div>
        <AlineAvatarGroup max={2}>
          {[...authorNamesSet].map(({ authorName, avatarColor }, index) => {
            return (
              <AlineAvatar
                key={index}
                avatarInfo={authorName.charAt(0).toUpperCase()}
                styles={{ backgroundColor: avatarColor ? avatarColor : theme.appColors.accent2 }}
              />
            );
          })}
        </AlineAvatarGroup>
      </div>
    </>
  );
}
